import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { 
  Upload, Camera, Trash2, Edit3, Plus, X, Eye, ExternalLink,
  ShieldCheck, MapPin, Globe, Instagram, Facebook, Phone, Mail, User,
  ShoppingBag, Info, Image as ImageIcon, MessageSquare, Star, Save, CheckCircle, Sparkles, Tag, Link as LinkIcon
} from "lucide-react";
import "./VendorAccount.css";

const CATEGORY_OPTIONS = [
  "Food & Beverage", "Fashion & Apparel", "Jewelry & Accessories",
  "Home & Decor", "Beauty & Wellness", "Art & Crafts",
  "Kids & Toys", "Books & Stationery", "Tech & Gadgets", "Services",
];

const LOGO_BUCKET    = "logos";
const COVER_BUCKET   = "vendor_covers";
const GALLERY_BUCKET = "vendor_gallery";

const MANUAL_FIELDS = [
  "brand_name", "contact_person", "phone_number", "website",
  "instagram", "facebook", "kra_pin", "default_description",
  "bio", "categories", "slug"
];

const slugify = (str) =>
  (str || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

const VendorAccount = () => {
  const { vendor, setVendor } = useOutletContext();
  const [form, setForm]       = useState(null);
  const [products, setProducts] = useState([]);
  const [ratings, setRatings]   = useState([]);

  // Active View Tab on the Live Storefront
  const [activeTab, setActiveTab] = useState("about");

  // Edit Modals State
  const [editingField, setEditingField] = useState(null); // 'bio' | 'details' | 'slug' | 'categories'
  const [customCategory, setCustomCategory] = useState("");
  
  // Statuses
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [error, setError]             = useState("");
  const [slugError, setSlugError]     = useState("");
  const [logoUploading, setLogoUploading]       = useState(false);
  const [coverUploading, setCoverUploading]     = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [uploadError, setUploadError]           = useState("");

  useEffect(() => {
    if (vendor) {
      setForm({
        brand_name:          vendor.brand_name || "",
        contact_person:      vendor.contact_person || "",
        phone_number:        vendor.phone_number || "",
        website:             vendor.website || "",
        instagram:           vendor.instagram || "",
        facebook:            vendor.facebook || "",
        kra_pin:             vendor.kra_pin || "",
        default_description: vendor.default_description || "",
        slug:                vendor.slug || "",
        bio:                 vendor.bio || "",
        categories:          vendor.categories || [],
        logo_path:           vendor.logo_path || null,
        logo_url:            vendor.logo_url || null,
        cover_path:          vendor.cover_path || null,
        cover_url:           vendor.cover_url || null,
        gallery_paths:       vendor.gallery_paths || [],
        show_contact_public: vendor.show_contact_public ?? true,
      });

      loadExtraVendorData(vendor.id, vendor.can_list_products);
    }
  }, [vendor]);

  const loadExtraVendorData = async (vendorId, canList) => {
    const { data: ratingData } = await supabase
      .from("vendor_ratings")
      .select("*")
      .eq("vendor_id", vendorId)
      .order("created_at", { ascending: false });

    if (ratingData) setRatings(ratingData);

    if (canList) {
      const { data: prods } = await supabase
        .from("products")
        .select("*")
        .eq("vendor_id", vendorId)
        .eq("status", "published");

      if (prods) setProducts(prods);
    }
  };

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
    if (key === "slug") setSlugError("");
  };

  const saveVendorFields = async (partial) => {
    const { data, error: err } = await supabase
      .from("vendors")
      .update(partial)
      .eq("id", vendor.id)
      .select()
      .single();

    if (err) {
      console.error("Auto-save failed:", err.message);
      return null;
    }

    setVendor((prev) => ({ ...prev, ...data }));
    setForm((f) => ({ ...f, ...data }));
    return data;
  };

  // ── Asset Upload Handlers ──
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError("");
    setLogoUploading(true);
    try {
      const ext  = file.name.split(".").pop();
      const path = `${vendor.id}/logo.${ext}`;
      const { error: upErr } = await supabase.storage.from(LOGO_BUCKET).upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(path);
      setForm((f) => ({ ...f, logo_path: path, logo_url: urlData.publicUrl }));
      await saveVendorFields({ logo_path: path, logo_url: urlData.publicUrl });
    } catch (err) {
      setUploadError("Logo upload failed: " + err.message);
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError("");
    setCoverUploading(true);
    try {
      const ext  = file.name.split(".").pop();
      const path = `${vendor.id}/cover.${ext}`;
      const { error: upErr } = await supabase.storage.from(COVER_BUCKET).upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from(COVER_BUCKET).getPublicUrl(path);
      setForm((f) => ({ ...f, cover_path: path, cover_url: urlData.publicUrl }));
      await saveVendorFields({ cover_path: path, cover_url: urlData.publicUrl });
    } catch (err) {
      setUploadError("Cover upload failed: " + err.message);
    } finally {
      setCoverUploading(false);
      e.target.value = "";
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadError("");
    setGalleryUploading(true);
    try {
      const newEntries = [];
      for (const file of files) {
        const safeName = file.name.replace(/\s+/g, "_");
        const path = `${vendor.id}/${Date.now()}_${safeName}`;
        const { error: upErr } = await supabase.storage.from(GALLERY_BUCKET).upload(path, file);
        if (upErr) continue;
        const { data: urlData } = supabase.storage.from(GALLERY_BUCKET).getPublicUrl(path);
        newEntries.push({ path, name: file.name, url: urlData.publicUrl });
      }
      const updatedGallery = [...form.gallery_paths, ...newEntries];
      setForm((f) => ({ ...f, gallery_paths: updatedGallery }));
      await saveVendorFields({ gallery_paths: updatedGallery });
    } catch (err) {
      setUploadError("Gallery upload failed: " + err.message);
    } finally {
      setGalleryUploading(false);
      e.target.value = "";
    }
  };

  const removeGalleryPhoto = async (entry) => {
    const updatedGallery = form.gallery_paths.filter(p => p.path !== entry.path);
    setForm((f) => ({ ...f, gallery_paths: updatedGallery }));
    saveVendorFields({ gallery_paths: updatedGallery });
    supabase.storage.from(GALLERY_BUCKET).remove([entry.path]).catch(() => {});
  };

  // ── Categories ──
  const toggleCategory = (cat) => {
    setForm((f) => {
      const has = f.categories.includes(cat);
      return { ...f, categories: has ? f.categories.filter(c => c !== cat) : [...f.categories, cat] };
    });
    setSaved(false);
  };

  const addCustomCategory = () => {
    const val = customCategory.trim();
    if (!val || form.categories.includes(val)) { setCustomCategory(""); return; }
    setForm((f) => ({ ...f, categories: [...f.categories, val] }));
    setCustomCategory("");
    setSaved(false);
  };

  const removeCategory = (cat) => {
    setForm((f) => ({ ...f, categories: f.categories.filter(c => c !== cat) }));
    setSaved(false);
  };

  // ── Save Manual Changes ──
  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSlugError("");

    const cleanSlug = slugify(form.slug);

    if (cleanSlug) {
      const { data: existing } = await supabase
        .from("vendors")
        .select("id")
        .eq("slug", cleanSlug)
        .neq("id", vendor.id)
        .maybeSingle();

      if (existing) {
        setSaving(false);
        setSlugError("That profile URL is already taken — try another.");
        return;
      }
    }

    const payload = {
      brand_name:          form.brand_name,
      contact_person:      form.contact_person,
      phone_number:        form.phone_number,
      website:             form.website,
      instagram:           form.instagram,
      facebook:            form.facebook,
      kra_pin:             form.kra_pin,
      default_description: form.default_description,
      bio:                 form.bio,
      categories:          form.categories,
      slug:                cleanSlug || null,
    };

    const { data, error: updateError } = await supabase
      .from("vendors")
      .update(payload)
      .eq("id", vendor.id)
      .select()
      .single();

    setSaving(false);

    if (updateError) {
      setError("Couldn't save your changes. Try again.");
      return;
    }

    setVendor((prev) => ({ ...prev, ...data }));
    setForm((f) => ({ ...f, slug: cleanSlug }));
    setSaved(true);
    setEditingField(null);
    setTimeout(() => setSaved(false), 2500);
  };

  if (!vendor || !form) {
    return <div className="va-loading">Loading Live Profile Studio…</div>;
  }

  const isDirty = MANUAL_FIELDS.some((key) => {
    if (Array.isArray(form[key])) {
      return JSON.stringify(form[key]) !== JSON.stringify(vendor[key] || []);
    }
    return (form[key] || "") !== (vendor[key] || "");
  });

  const avgRating = ratings.length > 0 
    ? (ratings.reduce((acc, curr) => acc + curr.rating, 0) / ratings.length).toFixed(1)
    : "5.0";

  return (
    <div className="va-admin-studio-root">
      {/* ── Top Admin Control Bar ── */}
      <div className="va-studio-bar font-family">
        <div className="va-bar-title-group">
          <span className="va-admin-pill"><Sparkles size={13} /> Admin Studio</span>
          <h1>Live Storefront Editor</h1>
        </div>

        <div className="va-bar-actions">
          {vendor.slug && (
            <a
              href={`/v/${vendor.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="va-btn-ghost-sm"
            >
              <Eye size={14} /> Open Public Page <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>

      {uploadError && <div className="va-error-alert">{uploadError}</div>}

      {/* ── LIVE INTERACTIVE STOREFRONT CANVAS ── */}
      <div className="bzv-storefront-wrapper va-canvas-frame">
        {/* Cover Hero with Direct Upload Overlay */}
        <div
          className="bzv-cover-hero va-editable-hero"
          style={form.cover_url ? { backgroundImage: `url(${form.cover_url})` } : undefined}
        >
          {!form.cover_url && <div className="bzv-cover-fallback" />}
          <div className="bzv-cover-shade" />

          <label className="va-hero-upload-overlay">
            <Camera size={18} />
            <span>{coverUploading ? "Uploading..." : form.cover_url ? "Change Banner Image" : "Upload Banner Image"}</span>
            <input type="file" accept="image/*" hidden onChange={handleCoverUpload} disabled={coverUploading} />
          </label>
        </div>

        {/* Brand Card Header */}
        <div className="bzv-brand-section">
          <div className="bzv-brand-card">
            {/* Avatar Logo with Inline Camera Overlay */}
            <div className="bzv-avatar-box va-editable-avatar">
              {form.logo_url ? (
                <img src={form.logo_url} alt="Logo" />
              ) : (
                <span className="bzv-avatar-text">{form.brand_name?.[0]?.toUpperCase() || "?"}</span>
              )}

              <label className="va-avatar-upload-overlay" title="Update Logo">
                <Camera size={16} />
                <input type="file" accept="image/*" hidden onChange={handleLogoUpload} disabled={logoUploading} />
              </label>
            </div>

            <div className="bzv-brand-details">
              <div className="bzv-title-row">
                <h1 className="bzv-brand-heading">{form.brand_name || "Your Brand Name"}</h1>
                <span className="bzv-badge-verified">
                  <ShieldCheck size={13} /> Verified Store
                </span>
                <button 
                  className="va-inline-edit-btn" 
                  onClick={() => setEditingField("details")}
                  title="Edit Brand Name & Details"
                >
                  <Edit3 size={12} /> Edit Details
                </button>
              </div>

              <div className="bzv-stats-row">
                <span className="bzv-stat-trigger">
                  <Star size={13} className="bzv-star-filled" /> {avgRating} ({ratings.length} reviews)
                </span>
                <span className="bzv-dot-divider">•</span>
                <span className="bzv-stat-text"><MapPin size={13} /> Nairobi, Kenya</span>
                <span className="bzv-dot-divider">•</span>
                <span className="va-slug-badge" onClick={() => setEditingField("slug")}>
                  bizbazevents.com/v/<strong>{form.slug || "set-slug"}</strong> <Edit3 size={11} />
                </span>
              </div>

              {/* Category Pills with Quick Add Button */}
              <div className="bzv-tag-row">
                {form.categories?.map((cat) => (
                  <span key={cat} className="bzv-category-pill">
                    {cat}
                    <button type="button" onClick={() => removeCategory(cat)} className="va-tag-remove">
                      <X size={10} />
                    </button>
                  </span>
                ))}
                <button 
                  type="button" 
                  className="va-add-tag-pill"
                  onClick={() => setEditingField("categories")}
                >
                  <Plus size={12} /> Edit Categories
                </button>
              </div>
            </div>

            {/* Social & Web Links */}
            <div className="bzv-social-actions">
              {form.website && (
                <span className="bzv-link-pill"><Globe size={14} /> Website</span>
              )}
              {form.instagram && (
                <span className="bzv-link-pill"><Instagram size={14} /> Instagram</span>
              )}
              <button className="bzv-link-pill" onClick={() => setEditingField("details")}>
                <Edit3 size={13} /> Edit Links
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bzv-navigation-tabs">
            <button
              type="button"
              className={`bzv-tab-item ${activeTab === "about" ? "active" : ""}`}
              onClick={() => setActiveTab("about")}
            >
              <Info size={15} />
              <span>About &amp; Bio</span>
            </button>

            <button
              type="button"
              className={`bzv-tab-item ${activeTab === "gallery" ? "active" : ""}`}
              onClick={() => setActiveTab("gallery")}
            >
              <ImageIcon size={15} />
              <span>Showcase Gallery ({form.gallery_paths.length})</span>
            </button>

            <button
              type="button"
              className={`bzv-tab-item ${activeTab === "shop" ? "active" : ""}`}
              onClick={() => setActiveTab("shop")}
            >
              <ShoppingBag size={15} />
              <span>Shop Products ({products.length})</span>
            </button>

            <button
              type="button"
              className={`bzv-tab-item ${activeTab === "reviews" ? "active" : ""}`}
              onClick={() => setActiveTab("reviews")}
            >
              <MessageSquare size={15} />
              <span>Reviews ({ratings.length})</span>
            </button>
          </div>
        </div>

        {/* ── Main Tab Content ── */}
        <main className="bzv-main-pane">
          {/* ABOUT TAB EDITING */}
          {activeTab === "about" && (
            <div className="bzv-tab-body bzv-about-stack">
              <div className="bzv-info-card va-editable-card">
                <div className="va-card-header-flex">
                  <h3 className="bzv-info-card-title">About {form.brand_name || "Your Brand"}</h3>
                  <button className="va-btn-text" onClick={() => setEditingField("bio")}>
                    <Edit3 size={13} /> Edit Bio Text
                  </button>
                </div>

                <p className="bzv-info-card-text font-family">
                  {form.bio || "No brand bio added yet. Click 'Edit Bio Text' to share your brand story!"}
                </p>
              </div>

              {/* Public Contact Visibility Control */}
              <div className="bzv-info-card va-editable-card font-family">
                <div className="va-toggle-header">
                  <div>
                    <h3 className="bzv-info-card-title">Public Contact Details</h3>
                    <p className="va-card-hint">
                      Control whether buyers can see your direct representative, phone number, and email.
                    </p>
                  </div>
                  <button
                    type="button"
                    className={`va-switch ${form.show_contact_public ? "va-switch--on" : ""}`}
                    onClick={async () => {
                      const newVal = !form.show_contact_public;
                      setForm((f) => ({ ...f, show_contact_public: newVal }));
                      await saveVendorFields({ show_contact_public: newVal });
                    }}
                  >
                    <span className="va-switch-knob" />
                  </button>
                </div>

                {form.show_contact_public && (
                  <div className="bzv-contact-list">
                    {form.contact_person && (
                      <div className="bzv-contact-item">
                        <User size={16} className="bzv-contact-icon" />
                        <div>
                          <span className="bzv-contact-sub">Contact Person</span>
                          <strong className="bzv-contact-val">{form.contact_person}</strong>
                        </div>
                      </div>
                    )}
                    {form.phone_number && (
                      <div className="bzv-contact-item">
                        <Phone size={16} className="bzv-contact-icon" />
                        <div>
                          <span className="bzv-contact-sub">Phone / WhatsApp</span>
                          <strong className="bzv-contact-val">{form.phone_number}</strong>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GALLERY TAB EDITING */}
          {activeTab === "gallery" && (
            <div className="bzv-tab-body">
              <div className="bzv-info-card va-editable-card">
                <div className="va-card-header-flex">
                  <div>
                    <h3 className="bzv-info-card-title">Showcase Gallery Portfolio</h3>
                    <p className="va-card-hint">Hover over photos to delete them or click the tile below to upload new photos.</p>
                  </div>
                  <label className="bzv-primary-action-btn va-upload-pill">
                    <Plus size={14} /> Add Photos
                    <input type="file" accept="image/*" multiple hidden onChange={handleGalleryUpload} disabled={galleryUploading} />
                  </label>
                </div>

                <div className="bzv-photo-grid va-admin-gallery-grid">
                  {form.gallery_paths.map((entry) => (
                    <div key={entry.path} className="va-gallery-tile">
                      <img src={entry.url} alt={entry.name} />
                      <button
                        type="button"
                        className="va-tile-delete-overlay"
                        onClick={() => removeGalleryPhoto(entry)}
                        title="Delete photo from gallery"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  ))}

                  <label className="va-gallery-tile-dropzone">
                    <Plus size={24} />
                    <span>{galleryUploading ? "Uploading..." : "Add Photo"}</span>
                    <input type="file" accept="image/*" multiple hidden onChange={handleGalleryUpload} disabled={galleryUploading} />
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* SHOP TAB */}
          {activeTab === "shop" && (
            <div className="bzv-tab-body font-family">
              <div className="bzv-info-card">
                <div className="va-card-header-flex">
                  <div>
                    <h3 className="bzv-info-card-title">Storefront Products ({products.length})</h3>
                    <p className="va-card-hint">Manage listings under the "Products" tab in your sidebar dashboard.</p>
                  </div>
                </div>

                {products.length === 0 ? (
                  <div className="bzv-empty-reviews-box">
                    <ShoppingBag size={32} className="bzv-muted-icon" />
                    <p>No published products yet.</p>
                  </div>
                ) : (
                  <div className="bzv-products-grid">
                    {products.map((item) => (
                      <div key={item.id} className="bzv-product-card">
                        <div className="bzv-card-image-wrap">
                          <img src={item.images?.[0]?.url || "/placeholder.png"} alt={item.title} />
                          <span className="bzv-card-price-tag">KES {Number(item.price).toLocaleString()}</span>
                        </div>
                        <div className="bzv-card-body">
                          <h3 className="bzv-card-title">{item.title}</h3>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* REVIEWS TAB */}
          {activeTab === "reviews" && (
            <div className="bzv-tab-body font-family">
              <div className="bzv-info-card">
                <h3 className="bzv-info-card-title">Customer Reviews ({ratings.length})</h3>
                {ratings.length === 0 ? (
                  <p className="va-card-hint">No customer reviews posted yet.</p>
                ) : (
                  <div className="bzv-reviews-list">
                    {ratings.map((r) => (
                      <div key={r.id} className="bzv-review-item">
                        <div className="bzv-review-header">
                          <strong className="bzv-reviewer-name">{r.reviewer_name}</strong>
                        </div>
                        {r.comment && <p className="bzv-review-text">{r.comment}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ── HIGH-END EDIT SLUG MODAL ── */}
      {editingField === "slug" && (
        <div className="va-modal-backdrop" onClick={() => setEditingField(null)}>
          <div className="va-modal-card font-family" onClick={(e) => e.stopPropagation()}>
            <div className="va-modal-header">
              <div className="va-modal-title-group">
                <div className="va-modal-icon-badge">
                  <LinkIcon size={18} />
                </div>
                <div>
                  <h3>Edit Web Address</h3>
                  <p className="va-modal-sub">Customize your live public storefront handle</p>
                </div>
              </div>
              <button className="va-modal-close-btn" onClick={() => setEditingField(null)}><X size={18} /></button>
            </div>

            <div className="va-modal-body">
              <div className="va-input-group">
                <label>Storefront Slug</label>
                <div className="va-slug-row">
                  <span className="va-slug-prefix">bizbazevents.com/v/</span>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => set("slug", e.target.value)}
                    placeholder="your-brand-name"
                  />
                </div>
              </div>
              {slugError && <p className="va-error-text">{slugError}</p>}
              <span className="va-hint">Only lowercase letters, numbers, and hyphens allowed.</span>
            </div>

            <div className="va-modal-footer">
              <button className="va-btn-secondary" onClick={() => setEditingField(null)}>Cancel</button>
              <button className="bzv-primary-action-btn" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Handle"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── HIGH-END EDIT BIO MODAL ── */}
      {editingField === "bio" && (
        <div className="va-modal-backdrop" onClick={() => setEditingField(null)}>
          <div className="va-modal-card font-family" onClick={(e) => e.stopPropagation()}>
            <div className="va-modal-header">
              <div className="va-modal-title-group">
                <div className="va-modal-icon-badge">
                  <Edit3 size={18} />
                </div>
                <div>
                  <h3>Brand Bio &amp; Story</h3>
                  <p className="va-modal-sub">Describe your business, heritage, or artisanal process</p>
                </div>
              </div>
              <button className="va-modal-close-btn" onClick={() => setEditingField(null)}><X size={18} /></button>
            </div>

            <div className="va-modal-body">
              <div className="va-input-group">
                <textarea
                  rows={6}
                  value={form.bio}
                  onChange={(e) => set("bio", e.target.value)}
                  placeholder="Share your brand story, craftsmanship, materials used, or mission..."
                />
              </div>
            </div>

            <div className="va-modal-footer">
              <button className="va-btn-secondary" onClick={() => setEditingField(null)}>Cancel</button>
              <button className="bzv-primary-action-btn" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Bio"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── HIGH-END EDIT BRAND DETAILS MODAL ── */}
      {editingField === "details" && (
        <div className="va-modal-backdrop" onClick={() => setEditingField(null)}>
          <div className="va-modal-card va-modal-card--wide font-family" onClick={(e) => e.stopPropagation()}>
            <div className="va-modal-header">
              <div className="va-modal-title-group">
                <div className="va-modal-icon-badge">
                  <User size={18} />
                </div>
                <div>
                  <h3>Brand Profile &amp; Contact Details</h3>
                  <p className="va-modal-sub">Update your business name, representative, and social handles</p>
                </div>
              </div>
              <button className="va-modal-close-btn" onClick={() => setEditingField(null)}><X size={18} /></button>
            </div>

            <div className="va-modal-body">
              <div className="va-form-section">
                <span className="va-section-title">Brand Identity</span>
                <div className="va-grid-2">
                  <div className="va-input-group">
                    <label>Brand Name</label>
                    <div className="va-icon-input">
                      <Sparkles size={15} className="va-input-icon" />
                      <input type="text" value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} />
                    </div>
                  </div>

                  <div className="va-input-group">
                    <label>Contact Representative</label>
                    <div className="va-icon-input">
                      <User size={15} className="va-input-icon" />
                      <input type="text" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="va-form-section">
                <span className="va-section-title">Contact &amp; Social Links</span>
                <div className="va-grid-2">
                  <div className="va-input-group">
                    <label>Phone / WhatsApp</label>
                    <div className="va-icon-input">
                      <Phone size={15} className="va-input-icon" />
                      <input type="text" value={form.phone_number} onChange={(e) => set("phone_number", e.target.value)} />
                    </div>
                  </div>

                  <div className="va-input-group">
                    <label>Website URL</label>
                    <div className="va-icon-input">
                      <Globe size={15} className="va-input-icon" />
                      <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} />
                    </div>
                  </div>

                  <div className="va-input-group">
                    <label>Instagram Handle</label>
                    <div className="va-icon-input">
                      <Instagram size={15} className="va-input-icon" />
                      <input type="text" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} />
                    </div>
                  </div>

                  <div className="va-input-group">
                    <label>Facebook Link</label>
                    <div className="va-icon-input">
                      <Facebook size={15} className="va-input-icon" />
                      <input type="text" value={form.facebook} onChange={(e) => set("facebook", e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="va-modal-footer">
              <button className="va-btn-secondary" onClick={() => setEditingField(null)}>Cancel</button>
              <button className="bzv-primary-action-btn" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Brand Info"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── HIGH-END EDIT CATEGORIES MODAL ── */}
      {editingField === "categories" && (
        <div className="va-modal-backdrop" onClick={() => setEditingField(null)}>
          <div className="va-modal-card font-family" onClick={(e) => e.stopPropagation()}>
            <div className="va-modal-header">
              <div className="va-modal-title-group">
                <div className="va-modal-icon-badge">
                  <Tag size={18} />
                </div>
                <div>
                  <h3>Select Primary Categories</h3>
                  <p className="va-modal-sub">Choose tags that best describe what your brand offers</p>
                </div>
              </div>
              <button className="va-modal-close-btn" onClick={() => setEditingField(null)}><X size={18} /></button>
            </div>

            <div className="va-modal-body">
              <div className="va-chip-grid">
                {CATEGORY_OPTIONS.map((cat) => (
                  <button
                    type="button"
                    key={cat}
                    className={`va-category-chip ${form.categories.includes(cat) ? "active" : ""}`}
                    onClick={() => toggleCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="va-custom-tag-section">
                <label>Add Custom Category Tag</label>
                <div className="va-custom-cat-row">
                  <input
                    type="text"
                    placeholder="e.g. Leathercraft, Vegan Cosmetics..."
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomCategory(); } }}
                  />
                  <button type="button" className="va-add-cat-btn" onClick={addCustomCategory}>
                    <Plus size={14} /> Add Tag
                  </button>
                </div>
              </div>
            </div>

            <div className="va-modal-footer">
              <button className="va-btn-secondary" onClick={() => setEditingField(null)}>Cancel</button>
              <button className="bzv-primary-action-btn" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Categories"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── FLOATING SAVE BAR ── */}
      {isDirty && (
        <div className="va-floating-bar font-family">
          <span className="va-floating-text">You have unsaved changes to your profile</span>
          <button className="va-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : <><Save size={15} /> Save Changes</>}
          </button>
        </div>
      )}

      {/* ── SAVED CONFIRMATION TOAST ── */}
      {saved && (
        <div className="va-saved-toast font-family">
          <CheckCircle size={15} /> Profile Saved Successfully
        </div>
      )}
    </div>
  );
};

export default VendorAccount;